import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Entity } from '../classes/entity.class';
import { Project } from '../classes/project.class';
import { Realisation, RealisationHourtype } from '../classes/realisation';
import { User } from '../classes/user.class';
import { LocalStorage } from '../storage.class';
import { Utils } from '../utils.class';
import { ApiService } from './api/api.service';
import { AuthorisationService } from './auth/authorisation.service';

@Injectable({ providedIn: 'root' })
export class RealisationService {
    // One source of truth for hourtype icons. Views choose whether they render this as solid or duotone.
    static hourtypeIconNames = new Map<RealisationHourtype, string>([
        [RealisationHourtype.frost, 'fa-piggy-bank'],
        [RealisationHourtype.time_for_time, 'fa-timer'],
        [RealisationHourtype.day_off, 'fa-person-walking-arrow-right'],
        [RealisationHourtype.short_absence, 'fa-calendar-clock'],
        [RealisationHourtype.paid_leave, 'fa-calendar-range'],
        [RealisationHourtype.illness, 'fa-face-thermometer'],
        [RealisationHourtype.education, 'fa-school'],
        [RealisationHourtype.bank_holiday, 'fa-party-horn'],
        [RealisationHourtype.sleep, 'fa-face-sleeping'],
        [RealisationHourtype.worktime, 'fa-person-digging'],
        [RealisationHourtype.driving_to, 'fa-truck'],
        [RealisationHourtype.driving_back, 'fa-truck fa-flip-horizontal'],
        [RealisationHourtype.travel_to, 'fa-car-side'],
        [RealisationHourtype.travel_back, 'fa-car-side fa-flip-horizontal'],
        [RealisationHourtype.special_leave, 'fa-baby'],
        [RealisationHourtype.additional_parental, 'fa-sparkles'],
        [RealisationHourtype.zzp_leave, 'fa-xmark'],
        [RealisationHourtype.not_available, 'fa-user-xmark'],
        [RealisationHourtype.loading_unloading, 'fa-truck-ramp-box'],
        [RealisationHourtype.loading_unloading_end, 'fa-truck-ramp-box fa-flip-horizontal'],
        [RealisationHourtype.repair_maintenance, 'fa-screwdriver-wrench'],
        [RealisationHourtype.waiting, 'fa-hourglass-half'],
        [RealisationHourtype.rain, 'fa-cloud-rain'],
        [RealisationHourtype.setting_out, 'fa-ruler-combined'],
        [RealisationHourtype.office, 'fa-building'],
    ]);

    constructor(protected apiService: ApiService) {}

    /** Pause minutes for a day; mirrors backend pause thresholds (see environment.pauseThresholds) */
    calculatePause(
        hourtype: RealisationHourtype,
        dayRealisations: Realisation[],
        bookedMinutes: number,
        additionalMinutes = 0
    ) {
        const { company, default: standard } = environment.pauseThresholds;
        const onlyOffice =
            hourtype === RealisationHourtype.office &&
            dayRealisations
                .filter(r => !r.removed && environment.worktimeHourTypes.includes(r.hourtype))
                .every(r => r.hourtype === RealisationHourtype.office);
        const total = bookedMinutes + additionalMinutes;

        return Object.entries((onlyOffice && company) || standard).reduce(
            (pause, [from, value]) => (total >= +from ? value : pause),
            0
        );
    }

    getUserDay(userId, date: Date) {
        return this.apiService.getCall$<{
            realisations: Realisation[];
            team: Entity;
        }>(`realisation/user/${userId}/${Utils.formatDate(date)}`);
    }

    getWeekReportPdf(
        afasProjectId: string,
        period: number,
        year: number,
        onlyWeekTotals: boolean,
        noHours: boolean,
        noComments: boolean,
        noAsphalt: boolean,
        noCars: boolean
    ) {
        return this.apiService.getBlobCall(
            `realisation/week-report-pdf/${afasProjectId}/${period}/${year}/${onlyWeekTotals}/${noHours}/${noComments}/${noAsphalt}/${noCars}`
        );
    }

    getProjectsPeriod(beginDate: Date, endDate: Date, alwaysInclude?: string, searchProjects?: string) {
        let params = {
            beginDate: new Date(beginDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
            alwaysInclude,
        } as any;
        if (searchProjects) {
            params = {
                ...params,
                searchProjects,
            };
        }
        return this.apiService.getCall$<Project[]>('realisation/period/projects', params);
    }

    getDayEntities(date: Date, userId: number) {
        return this.apiService.getCall$<number[]>('realisation/day-entities', {
            date: Utils.formatDate(date),
            userId,
        });
    }

    public getDayExecutors(date: Date) {
        return this.apiService.getCallData$<User[]>(`realisation/day-executors`, {
            date: Utils.formatDate(date),
        });
    }

    public getHistory<P>(id: number) {
        return this.apiService.getCall$<P[]>(`realisation/history`, {
            id,
        });
    }

    getProjectSchedule(beginDate: Date, endDate: Date, afas_project_id?: string) {
        let params = {
            beginDate: new Date(beginDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        } as any;
        if (afas_project_id) {
            params = {
                ...params,
                afas_project_id,
            };
        }
        return this.apiService.getCall$<Realisation[]>('realisation/schedule/project', params);
    }

    calcMaxDate(add?: number) {
        const maxDate = Utils.setTime(new Date(), 23, 59);
        if (environment.limitFutureOwnPlanningOverview) {
            if (!LocalStorage.getUser() || AuthorisationService.hasRole(environment.roles.TruckDriver)) {
                maxDate.setDate(maxDate.getDate() + 1);
                while (maxDate.getDay() < 1 || maxDate.getDay() > 5) {
                    maxDate.setDate(maxDate.getDate() + 1);
                }
            } else {
                maxDate.setDate(maxDate.getDate() + (7 - maxDate.getDay()));
                const nextWeekFromDay = 5;
                if (new Date().getDay() >= nextWeekFromDay) {
                    maxDate.setDate(maxDate.getDate() + 7);
                }
                if (add) {
                    maxDate.setDate(maxDate.getDate() + add);
                }
            }
        } else {
            maxDate.setFullYear(maxDate.getFullYear() + 999);
        }
        return maxDate;
    }

    getDaySchedule(date: number | string | Date, executorId?: number) {
        return this.apiService.getCall$<Realisation[]>('realisation/schedule/day', {
            date: Utils.formatDate(new Date(date)),
            executorId: executorId ?? '',
        });
    }

    getPreviousDayOpen(date: number | string | Date, executorId?: number, nextDay?: boolean) {
        return this.apiService.getCall$<RealisationCount[]>('realisation/schedule/previous-open', {
            date: Utils.formatDate(new Date(date)),
            executorId: executorId ?? '',
            nextDay,
        });
    }

    getFirstUnsubmittedDay(date: Date, userId?: number) {
        const params: any = {
            date: Utils.formatDate(date),
        };
        if (userId) {
            params.userId = userId;
        }
        return this.apiService.getCall$<string | null>('realisation/schedule/first-unsubmitted', params);
    }

    getUserSchedule(beginDate: Date, endDate: Date, userId?: number) {
        let params = {
            beginDate: Utils.formatDate(beginDate),
            endDate: Utils.formatDate(endDate),
        } as any;

        if (userId) {
            params = {
                ...params,
                userId,
            };
        }
        return this.apiService.getCall$<Realisation[]>('realisation/schedule/user', params);
    }

    getUserDaySchedule(date, userId?: number) {
        let params = {
            date: new Date(date).toISOString(),
        } as any;
        if (userId) {
            params = {
                ...params,
                userId,
            };
        }
        return this.apiService.getCall$<{
            realisations: Realisation[];
            user?: User;
        }>('realisation/schedule/user/day', params);
    }

    getUserDayTotals(date, userId?: number) {
        let params = {
            date: new Date(date).toISOString(),
        } as { date: string; userId?: number };
        if (userId) {
            params = {
                ...params,
                userId,
            };
        }
        return this.apiService.getCallData$<{
            weekTotal: DayWeekTotal;
            dayTotal: DayWeekTotal;
        }>('realisation/schedule/user/day/totals', params);
    }

    submitRealisation(realisations: Realisation[]) {
        return this.apiService.postCall$('realisation/schedule/submit', { realisations });
    }

    saveRealisation(realisation: Realisation) {
        return this.apiService.postCall$<{ realisation: Realisation }>('realisation/schedule/save', realisation);
    }

    moveRealisationsToPerformer(realisationIds: number[], performerId: number) {
        return this.apiService.postCall$<boolean>('realisation/schedule/move/performer', {
            realisationIds,
            performerId,
        });
    }

    approveRealisation(realisation: Realisation) {
        return this.apiService.postCall$<Realisation[]>('realisation/schedule/approve', {
            id: realisation.id,
            updated_at: realisation.updated_at,
            approved: realisation.approved,
            comment_user_approved_handled: realisation.comment_user_approved_handled,
        });
    }

    getPlanningRealisations(planningId: number) {
        return this.apiService.getCall$<Realisation[]>(`realisation/schedule/planning/${planningId}`);
    }

    hiringnames(type: 'entity' | 'user' | 'hiring_material' | 'hiring_user', search: string) {
        return this.apiService.getCall$<string[]>('realisation/hiringnames', {
            type: type ?? '',
            search: search ?? '',
        });
    }

    changePause(date: Date, pause: number, userId?: number) {
        let params = {
            date,
            pause,
        } as any;
        if (userId) {
            params = {
                ...params,
                userId,
            };
        }
        return this.apiService.postCall$<{ realisations: Realisation[] }>('realisation/schedule/pause', params);
    }
}

interface DayWeekTotal {
    minutes: number;
    pause: number;
}

export class RealisationCount {
    bookdate: Date;
    count: number;
}
